import React, { useEffect, useState } from "react";
import { FaArrowLeftLong } from "react-icons/fa6";
import { useNavigate } from "react-router-dom";
import {
  useAppDispatch,
  useAppSelector,
  LOGIN_ROUTE,
  setCurrentStep,
  setOtpSent,
  checkPhoneNumberExists,
  sendOTP,
  signUp,
  validateOtp,
  checkExistsEmail,
  emailSignUp,
  sendEmailOTP,
  login,
} from "@hypexone/uicore";
import StepOne from "./StepOne";
import Button from "../../components/shared/Button/Button";
import { useSignUpModal } from "../../context/SignUpModalContext";

const SignUpForm: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { setSignUpModalOpen } = useSignUpModal();

  const { otpSent, emailExists } = useAppSelector((state) => state.auth);
  const { langData } = useAppSelector((state) => state.cms);

  const [phoneNumber, setPhoneNumber] = useState<string>("");
  const [email, setEmail] = useState<string>(JSON.parse(localStorage.getItem("responseEmail") || ""));
  const [otp, setOtp] = useState<number>();
  const [agreedToTerms, setAgreedToTerms] = useState<boolean>(false);
  const [username, setUsername] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [loader, setLoader] = useState<boolean>(false);

  const searchParams = new URLSearchParams(location.search);
  const campaignId = searchParams.get("campaignId");
  const [campaignCode, setCampaignCode] = useState<string>(campaignId ?? "");
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    const maxLength = 10;
    if (newValue.length <= maxLength && /^\d*$/.test(newValue)) {
      setPhoneNumber(newValue);
    }
  };
    const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
  };
  const [isUsernameValid, setIsUsernameValid] = useState(false);

  const handleTermsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAgreedToTerms(e.target.checked);
  };

  const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUsername(e.target.value);
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
  };

  const showPasswordClickHandler = () => {
    setShowPassword((prev) => !prev);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const rawCamp = campaignCode ?? "";
    const cleanCamp = rawCamp.trim().toLowerCase();
    setLoader(true);

    try {
      if (!otpSent && email && agreedToTerms) {
        await dispatch(sendEmailOTP(email)).unwrap();
        dispatch(setOtpSent(true));
      } else if (otpSent && otp && (!username || !password)) {
        await dispatch(
          validateOtp({ email, otp })
        ).unwrap();
      } else if (otpSent && username && password && otp) {
        await dispatch(
          emailSignUp({
            username: username.toLowerCase(),
            fullName: username.toLowerCase(),
            emailId:email,
            password,
            otp,
            referralCode: "",
            campaignCode: cleanCamp,
          })
        ).unwrap();
        navigate("/home");
      }
    } catch (error) {
      console.error("Submission failed:", error);
    } finally {
      setLoader(false);
    }
  };

  const redirectToSignIn = () => {
    setSignUpModalOpen(false);
    navigate(LOGIN_ROUTE);
  };

  useEffect(() => {
    if (campaignId) {
      localStorage.setItem("campaignId", campaignId);
    }
  }, [campaignId]);

  useEffect(() => {
    dispatch(setCurrentStep(1));
    dispatch(setOtpSent(false));
  }, []);

  useEffect(() => {
    if (email) {
      dispatch(checkExistsEmail(email));
    }
  }, [email]);

  const isSubmitDisabled =
    !email ||
    !agreedToTerms ||
    !isUsernameValid ||
    emailExists ||
    (otpSent && (!otp || !username || !password)) ||
    loader;

  return (
   <main className="h-screen flex items-center justify-center   px-4 overflow-y-scroll hide-scrollbar">
      <div className="relative w-full max-w-md p-8 rounded-2xl  h-full ">
          <Button
                   onClick={redirectToSignIn}
                   className="absolute top-4 left-4 flex items-center gap-2 text-sm font-medium text-white hover:text-yellow-300 transition-all"
                 >
                   <FaArrowLeftLong className="text-base" />
                   <span>{langData?.["back"]}</span>
                 </Button>

          <header className="flex flex-col justify-center items-center text-center mb-2">
          <h1 className="text-3xl font-extrabold text-white drop-shadow-md">
            {langData?.["sign_up"]}
          </h1>
          <p className="text-base text-gray-200 mt-1">
            {langData?.["create_your_account_txt"]}
          </p>
        </header>

          <form
            onSubmit={handleSubmit}
          className="flex flex-col w-full text-sm font-medium space-y-4"
          >
            <StepOne
              otpSent={otpSent}
              otp={otp}
              phoneNumber={phoneNumber}
              agreedToTerms={agreedToTerms}
              email={email}
              onEmailChange={handleEmailChange}
              onPhoneChange={handlePhoneChange}
              onTermsChange={handleTermsChange}
              username={username}
              password={password}
              setOtp={setOtp}
              showPassword={showPassword}
              onUsernameChange={handleUsernameChange}
              onPasswordChange={handlePasswordChange}
              showPasswordClickHandler={showPasswordClickHandler}
              onUsernameValidityChange={setIsUsernameValid}
              campaignCode={campaignCode}
              onCampaignCodeChange={setCampaignCode}
            />

            <Button
              type="submit"
              disabled={isSubmitDisabled}
              loading={loader}
              className={`gap-2.5 self-stretch px-16 py-3 mt-5 text-base font-semibold text-center text-white rounded-lg min-h-[44px] shadow-[0px_1px_2px_rgba(0,0,0,0.05)] max-md:px-5 ${
                isSubmitDisabled
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-secondary hover:bg-blue-700"
              }`}
            >
              {!otpSent
                ? langData?.["send_otp"]
                : otpSent && (!username || !password)
                ? langData?.["validate_otp"]
                : langData?.["sign_up"]}
            </Button>

            <div className="self-center mt-6 text-sm text-center text-white">
              {langData?.["already_have_an_account"]}{" "}
              <a
                href="#"
                onClick={redirectToSignIn}
                className="text-yellow-300 underline font-medium hover:text-yellow-500 transition-all duration-200"
              >
                {langData?.["sign_in"]}
              </a>
            </div>
          </form>
      </div>
    </main>
  );
};

export default SignUpForm;
